import { useQuery } from "@tanstack/react-query";
import { client } from "../../utils/axiosClient";

const reportQueryConfig = {
  refetchInterval: 1800000,
  staleTime: 1800000,
  placeholderData: (prev: any) => prev,
};

export const useGetTenantInsights = () => {
  return useQuery({
    queryKey: ["tenantInsights"],
    queryFn: async () => {
      const { data } = await client.get("report/get-tenant-insights");
      return data;
    },
    ...reportQueryConfig,
  });
};

const buildReportQueryEnabled = (enabled, weeks, projectIds, userIds, teamIds) =>
  Boolean(
    enabled &&
    weeks?.length &&
    !(weeks.length === 1 && weeks[0]?.startDate === "" && weeks[0]?.endDate === "") &&
    projectIds?.length &&
    (userIds?.length || teamIds?.length)
  );

export const useGetTotalTimeVsEstimationPerProject = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["totalTimeVsEstimationPerProject", payload],
    queryFn: async () => {
      const { data } = await client.post("report/total-time-vs-estimation-per-project", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetTeamTimeOnProjects = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["teamTimeOnProjects", payload],
    queryFn: async () => {
      const { data } = await client.post("report/team-time-on-projects", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetTaskVsBugCountPerProject = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["taskVsBugCountPerProject", payload],
    queryFn: async () => {
      const { data } = await client.post("report/task-vs-bug-count-per-project", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetTaskVsBugTimePerProject = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["taskVsBugTimePerProject", payload],
    queryFn: async () => {
      const { data } = await client.post("report/task-vs-bug-time-per-project", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetTeamAllocationPerProject = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["teamAllocationPerProject", payload],
    queryFn: async () => {
      const { data } = await client.post("report/team-allocation-per-project", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetTotalTimeSpentByTeams = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["totalTimeSpentByTeams", payload],
    queryFn: async () => {
      const { data } = await client.post("report/total-time-spent-by-teams", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetTotalTimeVsEstimationPerUser = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["totalTimeVsEstimationPerUser", payload],
    queryFn: async () => {
      const { data } = await client.post("report/total-time-vs-estimation-per-user", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetAssigneeTimeOnProjects = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["assigneeTimeOnProjects", payload],
    queryFn: async () => {
      const { data } = await client.post("report/assignee-time-on-projects", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetTaskVsBugCountPerUser = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["taskVsBugCountPerUser", payload],
    queryFn: async () => {
      const { data } = await client.post("report/task-vs-bug-count-per-user", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useGetTaskVsBugTimePerUser = (weeks, projectIds, userIds, teamIds, enabled) => {
  const payload = { weeks, projectIds, userIds, teamIds };
  return useQuery({
    queryKey: ["taskVsBugTimePerUser", payload],
    queryFn: async () => {
      const { data } = await client.post("report/task-vs-bug-time-per-user", payload);
      return data;
    },
    enabled: buildReportQueryEnabled(enabled, weeks, projectIds, userIds, teamIds),
    ...reportQueryConfig,
  });
};

export const useCompareWeeklyTrends = (startDate, endDate, projectIds, userIds, enabled) => {
  const payload = { startDate, endDate, projectIds, userIds };
  return useQuery({
    queryKey: ["compareWeeklyTrends", payload],
    queryFn: async () => {
      const { data } = await client.post("report/compare-weekly-trends", payload);
      return data;
    },
    enabled: Boolean(enabled && startDate && endDate && projectIds?.length && userIds?.length),
    ...reportQueryConfig,
  });
};

export const useGetTeams = () => {
  return useQuery({
    queryKey: ["teams"],
    queryFn: async () => {
      const { data } = await client.get("teams/get-teams");
      return data;
    },
    ...reportQueryConfig,
  });
};